import { DAY_OF_WEEK } from '../data/Constants';
import { validateData } from './DataValidation';

import {
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInSeconds,
} from 'date-fns';

export const DATE_FORMAT = {
  APPOINTMENT: 'appointment',
  APPOINTMENT_SELECT: 'appointment-select',
  FULL: 'full',
  SHORT_YEAR: 'short-year',
};

const addZeroUnderTen = (num) => {
  return num < 10 ? `0${num}` : num;
};

export const formatRawMonth = (month) => {
  return addZeroUnderTen(month + 1);
};

export const formatRawDay = (day) => {
  return addZeroUnderTen(day);
};

export const formatDayOfWeek = (dow) => {
  return DAY_OF_WEEK[dow];
};

export const formatRawHour = (hour) => {
  if (hour === 0) return '12';
  else if (hour > 0 && hour < 10) return `0${hour}`;
  else if (hour > 12) return hour - 12;
  return hour;
};

export const formatRawMinutes = (minutes) => {
  return minutes < 9 ? `0${minutes}` : minutes;
};

export const formatShortYear = (year) => {
  return `${year}`.substring(`${year}`.length - 2);
};

export const formatMeridiem = (hours) => {
  return hours >= 12 ? 'PM' : 'AM';
};

/**
 * Calculates a formatted time string
 * @param {object|string} dateTime
 * @param {string} type - options referenced from the DATE_FORMAT object.
 * @param {string} timeZone - time zone, defaults to America/Chicago
 * @returns {string} formatted time string
 */
export const getFormattedTime = (dateTime, type, timeZone) => {
  const useDate = new Date(dateTime);
  const hour = formatRawHour(useDate.getHours());
  const minute = formatRawMinutes(useDate.getMinutes());
  const ampm = formatMeridiem(useDate.getHours());
  const timeZoneAbbrev = getTimezoneAbbreviation(useDate, timeZone);

  switch (type) {
    case DATE_FORMAT.APPOINTMENT_SELECT:
      return `${hour}:${minute} ${ampm} ${timeZoneAbbrev}`;
    case DATE_FORMAT.APPOINTMENT:
      return `${hour}:${minute} ${ampm} ${timeZoneAbbrev}`;
    default:
      return `${hour}:${minute} ${ampm} ${timeZoneAbbrev}`;
  }
};

/**
 * Calculates the formatted date string
 * @param {object|string} dateTime
 * @param {string} type - options referenced from the DATE_FORMAT object.
 * @returns {string} formatted date string
 */
export const getFormattedDate = (dateTime, type) => {
  const useDate = new Date(dateTime);
  const dayOfWeek = formatDayOfWeek(useDate.getDay());
  const month = formatRawMonth(useDate.getMonth());
  const day = formatRawDay(useDate.getDate());
  const shortYear = formatShortYear(useDate.getFullYear());
  const longYear = useDate.getFullYear();

  switch (type) {
    case DATE_FORMAT.APPOINTMENT_SELECT:
      return `${month}/${day}`;
    case DATE_FORMAT.APPOINTMENT:
      return `${month}/${day}`;
    case DATE_FORMAT.FULL:
      return `${dayOfWeek} ${month}/${day}/${shortYear}`;
    case DATE_FORMAT.SHORT_YEAR:
      return `${month}/${day}/${longYear}`;
    default:
      return `${month}/${shortYear}`;
  }
};

/**
 * Returns the formatted date time using the date, type, and time zone passed.
 * @param {object|string} dateTime
 * @param {string} type - options referenced from the DATE_FORMAT object.
 * @param {string} timeZone - time zone, defaults to America/Chicago
 * @returns {string} formatted date string
 */
export const getFormattedDateTime = (dateTime, type, timeZone = 'CST') => {
  const date = getFormattedDate(dateTime, type);
  const time = getFormattedTime(dateTime, type, timeZone);

  switch (type) {
    case DATE_FORMAT.APPOINTMENT_SELECT:
      return `${date} @ ${time}`;
    default:
      return `${date} ${time}`;
  }
};

/**
 * Returns the time left between the date and the total minutes passed.
 * @param {object|string} end
 * @param {number} totalMinutes
 * @returns {object}
 */
export const getTimeDifference = (end, totalMinutes) => {
  const dateBegin = new Date();
  const dateEnd = new Date(end);

  const minutes = differenceInMinutes(dateEnd, dateBegin);
  const seconds = differenceInSeconds(dateEnd, dateBegin);

  const difference = differenceInMilliseconds(dateEnd, dateBegin);
  const totalTimeInSeconds = Math.ceil(Number(totalMinutes) * 1000 * 60);

  const diff = (difference / totalTimeInSeconds) * 100;
  const percent = Math.round(diff);

  return {
    minutes: minutes,
    seconds: seconds - minutes * 60,
    percent: percent > 100 ? 100 : percent,
  };
};

/**
 * Increment the passed date by the increment parameter.
 * @param {object} date - date object
 * @param {int} [increment=1] - default is 1, number to increment by
 */
export const incrementByDay = (date, increment = 1) => {
  validateData(increment, 'number');
  return new Date(date).setDate(date.getDate() + increment);
};

/**
 * Decrement the passed date by the decrement parameter.
 * @param {object} date - date object
 * @param {int} [decrement=1] - default is 1, number to decrement by
 */
export const decrementByDay = (date, decrement = 1) => {
  validateData(decrement, 'number');
  return new Date(date).setDate(date.getDate() - decrement);
};

/**
 * @description This function takes a string with an assigned timezone and finds its diff with UTC
 * This lets you calculate the offset for an arbitary timezone that isn't the server or local time
 * from https://stackoverflow.com/questions/20712419/get-utc-offset-from-timezone-in-javascript
 * @param  {string} timeZone - name of the timezone
 * @param  {date} date - date object
 * @returns {number} date offset in hours
 */
export const getTimezoneOffset = (
  date = new Date(),
  timeZone = 'America/Chicago',
) => {
  // Get the time offset in MS
  const offsetMS = getTimezoneOffsetMS(date, timeZone);

  // return the time offset in hours
  return (offsetMS * 0.001) / 3600;
};

/**
 * Get the timezone offset in MS
 * @param {object} date
 * @param {string} timeZone
 * @returns {number} date offset in milliseconds
 */
export const getTimezoneOffsetMS = (
  date = new Date(),
  timeZone = 'America/Chicago',
) => {
  // Get the current timezone abbreviation
  // console.log({step: 1, type: typeof date, date, timeZone});
  const tz = getTimezoneAbbreviation(date, timeZone);

  if (tz == 'CST') {
    return -21600000;
  } else if (tz == 'CDT') {
    return -18000000;
  } else {
    throw new Error('Unexpected timezone');
  }
};

export const getTimezoneAbbreviation = (date, timeZone) => {
  const useDate = new Date(date);
  const formattedTz = useDate.toLocaleString('en', {
    timeZone,
    timeZoneName: 'short',
  });
  // console.log({step: 2, type: typeof useDate, formattedTz})
  return formattedTz.split(' ').slice(-1)[0];
};

/**
 * Convert the UTC date/time to a timezone aware date/time.
 * @param {object|string} date
 * @param {string} timeZone
 * @returns {object} new formatted date object
 */
export const convertUTCDate = (date, timeZone = 'America/Chicago') => {
  const useDate = new Date(date);

  // Get the current MS version of the date
  const utcMS = useDate.getTime();

  // calculate the timezone offset for CDT on this date
  const offset = getTimezoneOffsetMS(useDate, timeZone);

  // Calculate the new date with the tz difference in MS
  const newDate = new Date(utcMS + offset);

  return new Date(
    newDate.getUTCFullYear(),
    newDate.getUTCMonth(),
    newDate.getUTCDate(),
    newDate.getUTCHours(),
    newDate.getUTCMinutes(),
    '0',
  );
};

/**
 * Return a time value in MS where the time is {increment} {type(hour/minute/seconds)}
 * from now.
 * @param {string} type
 * @param {int} increment
 * @returns
 */
export const getTimeFromNowInMS = (type, increment) => {
  const dateObj = new Date();
  validateData(type, 'string');
  validateData(increment, 'number');

  // Increment by minutes
  if (type === 'minute') {
    dateObj.setMinutes(dateObj.getMinutes() + increment);
  }

  // All the options not setup yet
  else {
    throw new Error('Increment type not set up yet, kthx.');
  }

  return dateObj.getTime();
};
